import { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'

/** Seconds the server asked us to wait (429 / 503 `Retry-After`), else null. */
export function retryAfterMs(error: unknown): number | null {
  if (!(error instanceof ApiError)) return null
  const cause = error.cause as { response?: { headers?: Record<string, unknown> } } | undefined
  const raw = cause?.response?.headers?.['retry-after']
  if (raw == null) return null
  const seconds = Number(raw)
  if (Number.isFinite(seconds)) return Math.min(seconds, 60) * 1000
  const at = Date.parse(String(raw))
  return Number.isNaN(at) ? null : Math.max(0, Math.min(at - Date.now(), 60_000))
}

// Reads are fresh for 5 minutes, as in the mobile ResponseCache. Auth and not-found never retry; 429, 5xx and network errors
// retry after Retry-After or a short backoff, so a burst of tabs does not hammer the throttler.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.status === 401 || error.status === 403 || error.status === 404 || error.status === 400) return false
          if (error.status === 429) return failureCount < 2
        }
        return failureCount < 1
      },
      retryDelay: (attempt, error) => retryAfterMs(error) ?? Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: { retry: 0 },
  },
})
